import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { getExceptionToastDescription } from "@shared/utils/errors/error-utils";
import { createPaymentLogger } from "../payment-logger";

import {
  isPaymentOrchestrationError,
  isPaymentConfirmationError,
} from "../errorUtils";
import { recordPaymentError } from "./recordPaymentError";
import { ErrorContextHydrator } from "./ErrorContextHydrator";
import type { HydrationState } from "./ErrorContextHydrator";

const log = createPaymentLogger("handlePaymentError");

/**
 * Core payment error handling logic shared across PaymentManager and
 * gateway-driven flows (e.g. Yuno wallet).
 *
 * Handles toast notifications, Sentry logging, and error context hydration.
 * Does NOT throw or attempt recovery — callers decide those independently.
 */
export function handlePaymentError(
  error: unknown,
  hydrationState: HydrationState,
  context?: Record<string, unknown>
): void {
  if (isPaymentConfirmationError(error)) {
    // Breadcrumb only — no Sentry event. Confirmation errors are expected
    // (declines, rejections) and don't warrant alerts, but the breadcrumb
    // provides context if a subsequent error fires in the same session.
    log.warn(
      "Payment confirmation error (decline/rejection)",
      error.toErrorReport()
    );

    // Uses getExceptionToastDescription (reads .message) intentionally.
    // For confirmation errors the gateway's decline message IS the
    // user-facing message — both .message and .userMessage are set to
    // the same value at creation in handleConfirmError.
    NotifierFactory.createNotifier().error(getExceptionToastDescription(error));
    return;
  }

  if (isPaymentOrchestrationError(error)) {
    ErrorContextHydrator.applyMetadata(error, hydrationState, context);

    log.error("Payment orchestration error occurred", error.toErrorReport());
    recordPaymentError(error);

    NotifierFactory.createNotifier().error(error.userMessage);
    return;
  }

  const userMessage =
    "Something went wrong with your payment. Please try again.";
  log.error("Unexpected payment error", { context });
  recordPaymentError(error);
  NotifierFactory.createNotifier().error(userMessage);
}
